import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Icon, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS, ReactTable } from "components";
import "modules/AcsesLogsDashboard/components/AcsesLogsTable/AcsesLogsTable.scss";
import { AcsesResponseModal } from "modules/AcsesLogsDashboard/components/AcsesLogsTable/components";
import { useAcsesLogsQuery } from "modules/AcsesLogsDashboard/hooks";
import { formatDate } from "utils/dateHelperFunctions";

import type { WebhookHistory } from "modules/AcsesLogsDashboard/queryKeys";

const columnHelper = createColumnHelper<WebhookHistoryWithParsedSentContent>();

type WebhookHistorySentContentBooking = {
  name: string;
  tag: string;
  state: string;
};

type WebhookHistorySentContent = {
  lockersystem: string;
  cellnumber: string;
  eventName: string;
  eventDate: string;
  booking: WebhookHistorySentContentBooking;
};

export type WebhookHistoryWithParsedSentContent = Omit<WebhookHistory, "sentContent"> & {
  sentContent: WebhookHistorySentContent;
};

export const AcsesLogsTable = () => {
  const { t } = useTranslation();
  const [selectedResponseContent, setSelectedResponseContent] = useState("");

  const { data: acsesLogs, isLoading: isLoadingAcsesLogs, error: loadingAcsesLogsError } = useAcsesLogsQuery();

  useEffect(() => {
    if (loadingAcsesLogsError?.message) toast.error(loadingAcsesLogsError.message);
  }, [loadingAcsesLogsError]);

  const columns = [
    columnHelper.accessor(row => row, {
      id: "locker_system",
      header: t("v8_locker_id", "Locker ID"),
      cell: ({ row: { original } }) => <span>{original.sentContent.lockersystem}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "cell_number",
      header: `${t("v8_cell", "Cell")} #`,
      cell: ({ row: { original } }) => <span>{original.sentContent.cellnumber}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "event_name",
      header: t("v8_event", "Event"),
      cell: ({ row: { original } }) => <span className="capitalize-text">{original.sentContent.eventName}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "event_date",
      header: t("v8_date", "Date"),
      cell: ({ row: { original } }) => <span>{formatDate(original.sentContent.eventDate, DATE_FORMATS_FNS.dateMonthYear)}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "wo_nr",
      header: `${t("v8_wo", "WO")}#`,
      cell: ({ row: { original } }) => (
        <Popup content={original.sentContent.booking.name} trigger={<div className="acses-logs-table-ellipsis">{original.sentContent.booking.name}</div>} />
      )
    }),
    columnHelper.accessor(row => row, {
      id: "license_plate",
      header: t("v8_license_plate", "License plate"),
      cell: ({ row: { original } }) => <span>{original.sentContent.booking.tag}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "state",
      header: t("v8_state", "State"),
      cell: ({ row: { original } }) => <span>{original.sentContent.booking.state}</span>
    }),
    columnHelper.accessor(row => row, {
      id: "claire_response",
      header: t("v8_claire_response", "Claire Response"),
      cell: ({ row: { original } }) => (
        <div className="acses-logs-table-claire-response-column">
          {original.responseStatus === 200 ? (
            <Icon className="check" color="green" />
          ) : (
            <Icon className="xmark" color="red" onClick={() => setSelectedResponseContent(original.responseContent)} />
          )}
        </div>
      )
    })
  ];

  return (
    <>
      <div className="AcsesLogsTable">
        <ReactTable columns={columns} isFetching={isLoadingAcsesLogs} data={acsesLogs} />
      </div>
      <AcsesResponseModal responseContent={selectedResponseContent} onClose={() => setSelectedResponseContent("")} />
    </>
  );
};
